import { endOf, startOf } from '@/utils/function'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { DateField, Typogaphy } from 'vnetwork-ui'

export interface DateRange {
    gte: string // YYYY-MM-DD format
    lte: string // YYYY-MM-DD format
}

export interface CustomDateRangeOption {
    value: string
    label: string
    calculate: () => DateRange
    detect?: (range: DateRange) => boolean
}

interface DateRangeSelectorProps {
    value?: DateRange
    onChange: (range: DateRange) => void
    className?: string
    label?: string
    placeholder?: string
    placement?: 'left' | 'right'
    excludeOptions?: DateRangeOption[]
    customOptions?: CustomDateRangeOption[]
    minDate?: string // YYYY-MM-DD format
    maxDate?: string // YYYY-MM-DD format
    /** Allow empty value without auto-setting default (default: false) */
    allowEmpty?: boolean
}

type DateRangeOption =
    | 'today'
    | 'yesterday'
    | 'this_month'
    | 'last_30_days'
    | 'last_month'
    | 'last_3_months'
    | 'last_6_months'
    | 'last_year'
    | 'custom'

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
    value,
    onChange,
    className = '',
    label,
    placement = 'left',
    excludeOptions = [],
    customOptions = [],
    minDate,
    maxDate,
    allowEmpty = false,
}) => {
    const { t } = useTranslation()
    // Initialize to undefined when allowEmpty, otherwise default to 'last_month'
    const [selectedOption, setSelectedOption] = useState<
        DateRangeOption | undefined
    >(allowEmpty ? undefined : 'last_month')
    const [isOpen, setIsOpen] = useState(false)
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    })
    const [dateError, setDateError] = useState<string | null>(null)

    const triggerRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Calculate dropdown position when opening
    const updateDropdownPosition = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4, // 4px gap
                left:
                    placement === 'left'
                        ? rect.left + window.scrollX
                        : rect.right + window.scrollX,
                width: rect.width,
            })
        }
    }, [placement])

    const defaultDateRangeOptions = [
        { value: 'today', label: 'date_range.today' },
        { value: 'yesterday', label: 'date_range.yesterday' },
        { value: 'this_month', label: 'date_range.this_month' },
        { value: 'last_30_days', label: 'date_range.last_30_days' },
        { value: 'last_month', label: 'date_range.last_month' },
        { value: 'last_3_months', label: 'date_range.last_3_months' },
        { value: 'last_6_months', label: 'date_range.last_6_months' },
        { value: 'last_year', label: 'date_range.last_year' },
    ]

    // Combine default and custom options
    const allDateRangeOptions = [
        ...defaultDateRangeOptions,
        ...customOptions.map((opt) => ({ value: opt.value, label: opt.label })),
    ]

    const dateRangeOptions = allDateRangeOptions.filter(
        (option) => !excludeOptions.includes(option.value as DateRangeOption)
    )

    const getSelectedLabel = () => {
        // Show placeholder when allowEmpty and no value
        if (allowEmpty && !value) {
            return t('date_range.select_period')
        }
        if (selectedOption === 'custom') {
            return `${dayjs(value?.gte).format('DD/MM/YYYY')} — ${dayjs(value?.lte).format('DD/MM/YYYY')}`
        }
        return t(
            dateRangeOptions.find((opt) => opt.value === selectedOption)
                ?.label || 'date_range.last_month'
        )
    }

    const calculateDateRange = (option: DateRangeOption): DateRange => {
        // Check if it's a custom option first
        const customOption = customOptions.find((opt) => opt.value === option)
        if (customOption) {
            return customOption.calculate()
        }

        const now = dayjs()

        switch (option) {
            case 'today':
                return {
                    gte: startOf(now, 'day'),
                    lte: endOf(now, 'day'),
                }

            case 'yesterday': {
                const yesterday = now.subtract(1, 'day')
                return {
                    gte: startOf(yesterday, 'day'),
                    lte: endOf(yesterday, 'day'),
                }
            }

            case 'this_month':
                return {
                    gte: startOf(now, 'month'),
                    lte: endOf(now, 'month'),
                }

            case 'last_month': {
                // check to day is not last day of month
                const lastMonth = now.subtract(1, 'month')
                if (now.date() === now.endOf('month').date()) {
                    return {
                        gte: startOf(now, 'month'),
                        lte: endOf(now, 'month'),
                    }
                }
                return {
                    gte: startOf(lastMonth, 'month'),
                    lte: endOf(lastMonth, 'month'),
                }
            }

            case 'last_3_months': {
                const threeMonthsAgo = now.subtract(3, 'month')
                if (now.date() === now.endOf('month').date()) {
                    return {
                        gte: startOf(now, 'month'),
                        lte: endOf(now, 'month'),
                    }
                }
                return {
                    gte: startOf(threeMonthsAgo, 'month'),
                    lte: endOf(threeMonthsAgo, 'month'),
                }
            }

            case 'last_6_months': {
                const sixMonthsAgo = now.subtract(6, 'month')
                if (now.date() === now.endOf('month').date()) {
                    return {
                        gte: startOf(now, 'month'),
                        lte: endOf(now, 'month'),
                    }
                }
                return {
                    gte: startOf(sixMonthsAgo, 'month'),
                    lte: endOf(sixMonthsAgo, 'month'),
                }
            }

            case 'last_year': {
                const lastYear = now.subtract(1, 'year')
                return {
                    gte: lastYear.startOf('year').format('YYYY-MM-DD'),
                    lte: lastYear.endOf('year').format('YYYY-MM-DD'),
                }
            }

            case 'last_30_days': {
                const thirtyDaysAgo = now.subtract(30, 'day')
                return {
                    gte: startOf(thirtyDaysAgo, 'day'),
                    lte: endOf(now, 'day'),
                }
            }

            case 'custom':
                if (value?.gte && value?.lte) {
                    return {
                        gte: dayjs(value?.gte)
                            .startOf('day')
                            .format('YYYY-MM-DD'),
                        lte: dayjs(value?.lte)
                            .endOf('day')
                            .format('YYYY-MM-DD'),
                    }
                } else {
                    const defaultLastMonth = now.subtract(1, 'month')
                    return {
                        gte: defaultLastMonth
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                        lte: defaultLastMonth
                            .endOf('month')
                            .format('YYYY-MM-DD'),
                    }
                }

            default: {
                const defaultLastMonth = now.subtract(1, 'month')
                return {
                    gte: defaultLastMonth.startOf('month').format('YYYY-MM-DD'),
                    lte: defaultLastMonth.endOf('month').format('YYYY-MM-DD'),
                }
            }
        }
    }

    // Helper function to validate and constrain date range
    const validateAndConstrainRange = (range: DateRange): DateRange => {
        const constrainedRange = { ...range }

        // Apply minDate constraint
        if (minDate && dayjs(constrainedRange.gte).isBefore(dayjs(minDate))) {
            constrainedRange.gte = minDate
        }

        // Apply maxDate constraint
        if (maxDate && dayjs(constrainedRange.lte).isAfter(dayjs(maxDate))) {
            constrainedRange.lte = maxDate
        }

        // Ensure start date doesn't exceed end date after constraints
        if (dayjs(constrainedRange.gte).isAfter(dayjs(constrainedRange.lte))) {
            if (minDate && maxDate) {
                // If both constraints exist and conflict, prioritize maxDate for end
                constrainedRange.gte = constrainedRange.lte
            }
        }

        return constrainedRange
    }

    const handleOptionChange = (option: DateRangeOption) => {
        setSelectedOption(option)
        setIsOpen(false)

        if (option === 'custom') {
            if (value && value.gte && value.lte) {
                const startDate = dayjs(value.gte).toDate()
                const endDate = dayjs(value.lte).toDate()
                const range = {
                    gte: dayjs(startDate).startOf('day').format('YYYY-MM-DD'),
                    lte: dayjs(endDate).endOf('day').format('YYYY-MM-DD'),
                }
                onChange(validateAndConstrainRange(range))
            }
        } else {
            const range = calculateDateRange(option)
            onChange(validateAndConstrainRange(range))
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node

            // Check if click is inside trigger or dropdown
            if (triggerRef.current && triggerRef.current.contains(target)) {
                return
            }
            if (dropdownRef.current && dropdownRef.current.contains(target)) {
                return
            }

            // Check if click is on a date picker modal (common class names for date pickers)
            const element = target as Element
            if (element && element.closest) {
                // Check for common date picker modal selectors - covers Ant Design, vnetwork-ui, and other libraries
                if (element.closest('[data-tippy-root]')) {
                    return
                }
            }

            setIsOpen(false)
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    // Update position when opening or on scroll/resize
    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition()
            window.addEventListener('scroll', updateDropdownPosition, true)
            window.addEventListener('resize', updateDropdownPosition)
            return () => {
                window.removeEventListener(
                    'scroll',
                    updateDropdownPosition,
                    true
                )
                window.removeEventListener('resize', updateDropdownPosition)
            }
        }
    }, [isOpen, updateDropdownPosition])

    const detectDateRangeOption = (range: DateRange): DateRangeOption => {
        if (!range.gte || !range.lte) return 'last_month'

        // Check custom options first
        for (const customOption of customOptions) {
            if (customOption.detect && customOption.detect(range)) {
                return customOption.value as DateRangeOption
            }
        }

        const now = dayjs()
        const startDate = dayjs(range.gte)
        const endDate = dayjs(range.lte)

        // Check for today
        if (startDate.isSame(now, 'day') && endDate.isSame(now, 'day')) {
            return 'today'
        }

        // Check for yesterday
        const yesterday = now.subtract(1, 'day')
        if (
            startDate.isSame(yesterday, 'day') &&
            endDate.isSame(yesterday, 'day')
        ) {
            return 'yesterday'
        }

        // Check for this month
        if (
            startOf(now, 'month') === range.gte &&
            endOf(now, 'month') === range.lte
        ) {
            return 'this_month'
        }

        // Check for last month
        const lastMonth = now.subtract(1, 'month')
        if (
            startOf(lastMonth, 'month') === range.gte &&
            endOf(lastMonth, 'month') === range.lte
        ) {
            return 'last_month'
        }

        // Check for last 3 months
        const threeMonthsAgo = now.subtract(3, 'month')
        if (
            startOf(threeMonthsAgo, 'month') === range.gte &&
            endOf(threeMonthsAgo, 'month') === range.lte
        ) {
            return 'last_3_months'
        }

        // Check for last 6 months
        const sixMonthsAgo = now.subtract(6, 'month')
        if (
            startOf(sixMonthsAgo, 'month') === range.gte &&
            endOf(sixMonthsAgo, 'month') === range.lte
        ) {
            return 'last_6_months'
        }

        // Check for last year
        const lastYear = now.subtract(1, 'year')
        if (
            lastYear.startOf('year').format('YYYY-MM-DD') === range.gte &&
            lastYear.endOf('year').format('YYYY-MM-DD') === range.lte
        ) {
            return 'last_year'
        }

        // Check for last 30 days
        const thirtyDaysAgo = now.subtract(30, 'day')
        if (
            startOf(thirtyDaysAgo, 'day') === range.gte &&
            endOf(now, 'day') === range.lte
        ) {
            return 'last_30_days'
        }

        // If none match, it's custom
        return 'custom'
    }

    useEffect(() => {
        if (!value) {
            // Skip auto-default if allowEmpty is true
            if (!allowEmpty) {
                const initialRange = calculateDateRange('last_month')
                onChange(validateAndConstrainRange(initialRange))
            } else {
                // Reset selectedOption when value is cleared
                setSelectedOption(undefined)
            }
        } else {
            // Validate current value against constraints
            const validatedRange = validateAndConstrainRange(value)

            // If the range was modified by constraints, update it
            if (
                validatedRange.gte !== value.gte ||
                validatedRange.lte !== value.lte
            ) {
                onChange(validatedRange)
            }

            // Detect the option based on current value
            const detectedOption = detectDateRangeOption(validatedRange)
            setSelectedOption(detectedOption)
        }
    }, [value?.gte, value?.lte, minDate, maxDate, allowEmpty])

    // Dropdown content rendered via Portal
    const dropdownContent =
        isOpen &&
        createPortal(
            <div
                ref={dropdownRef}
                className="bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto"
                style={{
                    position: 'absolute',
                    top: dropdownPosition.top,
                    left: placement === 'left' ? dropdownPosition.left : 'auto',
                    right:
                        placement === 'right'
                            ? window.innerWidth - dropdownPosition.left
                            : 'auto',
                    zIndex: 9999,
                    minWidth: dropdownPosition.width,
                }}
            >
                <div className="p-3 border-b border-gray-100">
                    <div className="flex gap-2 items-center">
                        <DateField
                            className="w-[130px]"
                            value={value?.gte ? dayjs(value?.gte) : undefined}
                            minDate={minDate ? dayjs(minDate) : undefined}
                            maxDate={
                                value?.lte
                                    ? dayjs(value.lte)
                                    : maxDate
                                      ? dayjs(maxDate)
                                      : undefined
                            }
                            onChange={(newVal: Dayjs | undefined) => {
                                const newStart = newVal ? newVal.toDate() : null
                                if (newStart) {
                                    setSelectedOption('custom')
                                    setDateError(null)
                                    const endDate = value?.lte
                                        ? dayjs(value.lte)
                                        : dayjs(newStart)
                                    const range: DateRange = {
                                        gte: dayjs(newStart)
                                            .startOf('day')
                                            .format('YYYY-MM-DD'),
                                        lte: endDate
                                            .endOf('day')
                                            .format('YYYY-MM-DD'),
                                    }
                                    onChange(validateAndConstrainRange(range))
                                }
                            }}
                        />
                        <Typogaphy className="text-gray-500">—</Typogaphy>
                        <DateField
                            className="w-[130px]"
                            value={value?.lte ? dayjs(value?.lte) : undefined}
                            minDate={
                                value?.gte
                                    ? dayjs(value.gte)
                                    : minDate
                                      ? dayjs(minDate)
                                      : undefined
                            }
                            maxDate={maxDate ? dayjs(maxDate) : undefined}
                            onChange={(newVal: Dayjs | undefined) => {
                                const newEnd = newVal ? newVal.toDate() : null
                                if (newEnd) {
                                    setSelectedOption('custom')
                                    setDateError(null)
                                    const startDate = value?.gte
                                        ? dayjs(value.gte)
                                        : dayjs(newEnd)
                                    const range: DateRange = {
                                        gte: startDate
                                            .startOf('day')
                                            .format('YYYY-MM-DD'),
                                        lte: dayjs(newEnd)
                                            .endOf('day')
                                            .format('YYYY-MM-DD'),
                                    }
                                    onChange(validateAndConstrainRange(range))
                                }
                            }}
                        />
                    </div>
                    {dateError && (
                        <div className="text-xs text-red-500 mt-1.5">
                            {dateError}
                        </div>
                    )}
                </div>

                <div className="py-1">
                    {dateRangeOptions.map((option) => (
                        <button
                            key={option.value}
                            className={`w-full flex items-center justify-center px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                                selectedOption === option.value
                                    ? 'bg-blue-50 text-blue-600 font-medium'
                                    : 'text-gray-700'
                            }`}
                            onClick={() =>
                                handleOptionChange(
                                    option.value as DateRangeOption
                                )
                            }
                        >
                            {t(option.label)}
                        </button>
                    ))}
                </div>
            </div>,
            document.body
        )

    return (
        <div className={`${className} relative`}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t(label)}
                </label>
            )}

            <div
                ref={triggerRef}
                className="relative cursor-pointer bg-white border border-gray-300 rounded-[4px] px-3 py-2 text-sm text-gray-700 hover:border-blue-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-[200px] flex items-center justify-between"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className={allowEmpty && !value ? 'text-gray-400' : ''}>
                    {getSelectedLabel()}
                </span>
                <svg
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </div>

            {dropdownContent}
        </div>
    )
}

export default DateRangeSelector
